import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Receipt, Calendar, Building, Mail, CreditCard, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const PublicAdminInvoicePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/public/admin-invoice/${token}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invoice not found');
      }
      setIsLoading(false);
    };
    load();
  }, [token]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-lg">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Invoice Not Found</h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const { invoice, agency, client } = data;
  const isPaid = invoice.status === 'paid';

  const statusColors = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700',
    overdue: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4" data-testid="public-admin-invoice-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">{agency.company_name}</h1>
          {agency.billing_email && (
            <p className="text-sm text-muted-foreground mt-1">{agency.billing_email}</p>
          )}
        </div>

        {/* Invoice Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="admin-invoice-card">
          {/* Status Banner */}
          {isPaid ? (
            <div className="bg-green-50 border-b border-green-100 px-6 py-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-800">Paid in Full</p>
                <p className="text-sm text-green-600">Paid on {invoice.paid_date} via {invoice.payment_method}</p>
              </div>
            </div>
          ) : invoice.status === 'overdue' ? (
            <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-800">Payment Overdue</p>
                <p className="text-sm text-red-600">This invoice was due on {invoice.due_date}</p>
              </div>
            </div>
          ) : null}

          <div className="p-6 sm:p-8 space-y-6">
            {/* Invoice header */}
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Invoice</p>
                <p className="text-lg font-mono font-semibold text-slate-900" data-testid="invoice-id">{invoice.id}</p>
              </div>
              <Badge className={statusColors[invoice.status] || statusColors.draft} data-testid="invoice-status">
                {invoice.status}
              </Badge>
            </div>

            {/* Bill To */}
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Bill To</p>
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-slate-400" />
                <p className="font-medium" data-testid="client-org-name">{client.org_name}</p>
              </div>
              {client.email && (
                <div className="flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <p className="text-sm text-muted-foreground">{client.email}</p>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Description</span>
                <span className="font-medium text-right max-w-[60%]">{invoice.description}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Period</span>
                <span className="font-medium">{invoice.period_start} to {invoice.period_end}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Due Date
                </span>
                <span className="font-medium">{invoice.due_date}</span>
              </div>
              {invoice.notes && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Notes</span>
                  <span className="text-right max-w-[60%]">{invoice.notes}</span>
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-slate-700">Amount Due</span>
                <span className="text-3xl font-bold text-slate-900" data-testid="invoice-amount">
                  ${invoice.amount?.toFixed(2)}
                </span>
              </div>
              {isPaid && (
                <div className="flex justify-between items-center mt-2 text-green-600">
                  <span className="text-sm">Amount Paid</span>
                  <span className="font-semibold">${invoice.paid_amount?.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Payment Info */}
            {!isPaid && (
              <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <p className="font-medium text-blue-900">Payment Information</p>
                </div>
                {agency.cashapp_tag && (
                  <p className="text-sm text-blue-800">CashApp: <strong>{agency.cashapp_tag}</strong></p>
                )}
                {agency.billing_email && (
                  <p className="text-sm text-blue-800">
                    Contact <strong>{agency.billing_email}</strong> for payment arrangements
                  </p>
                )}
                {!agency.cashapp_tag && !agency.billing_email && (
                  <p className="text-sm text-blue-800">
                    Please contact {agency.company_name} to arrange payment.
                  </p>
                )}
              </div>
            )}

            {/* Receipt section for paid */}
            {isPaid && invoice.payment_reference && (
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-xs text-green-600 uppercase tracking-wider mb-1">Payment Reference</p>
                <p className="font-mono text-sm text-green-800">{invoice.payment_reference}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          Powered by BirdenMedia
        </p>
      </div>
    </div>
  );
};

export default PublicAdminInvoicePage;
